import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart,
  CalendarDate,
  Laptop,
  PersonCircle,
} from "react-bootstrap-icons";
import { toast } from "react-toastify";
import { ABORT_ERROR } from "../../helpers/api";
import {
  ADMIN_ROLE,
  DEFAULT_PAGE_SIZE,
  MANAGER_ROLE,
} from "../../helpers/constants";
import { userTimeLogTableColumns } from "../../helpers/tableColumns";
import useAppChoices from "../../hooks/useAppChoices";
import useAuth from "../../hooks/useAuth";
import { timerTrackerService } from "../../services/timeTrackerServices";
import { userServices } from "../../services/userServices";
import { calculatePayment } from "../../Util/calculatePayment";
import AlertModal from "../AlertModal";
import DataTable from "../common/data-table/DataTable";
import TimeLogActions from "./TimeLogActions";
import ViewStatByJob from "./ViewStatByJob";
import ViewStatByUser from "./ViewStatByUser";
import AddEditTimeTrackerModal from "./AddEditTimeTrackerModal";
import { isAdmin, updateItemsInArray } from "../../helpers/global";
import FinancialDashboard from "./FinancialDashboard";

export const findIfObjectIsInArrayOrNot = (array, object, key = "_id") => {
  return array?.find((c) => c?.[key] === object?.[key]) || false;
};

const defaultDateRange = {
  startDate: moment()
    .startOf("week")
    .toDate(),
  endDate: moment()
    .endOf("week")
    .toDate(),
  key: "selection",
  rangeType: "Week",
};

const initialPageInfo = {
  pageNumber: 1,
  totalPages: 1,
};

const BrowseUserTimeLogs = () => {
  const users = useAppChoices("users");
  const { user } = useAuth();

  const abortControllerRef = useRef(null);

  //filters and action
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [includeDisabledLogs, setIncludeDisabledLogs] = useState(false);

  const [deleteLogMeta, setDeleteLogMeta] = useState(null);
  const [totalCost, setTotalCost] = useState(0);
  const [activeView, setActiveView] = useState(
    isAdmin(user?.role) ? "user" : "allTimeLogs"
  );
  const [addEditTimeLogMeta, setAddEditTimeLogMeta] = useState(null);

  // pagination
  const [toLoadUserLogsPageInfo, setToLoadUserLogsPageInfo] = useState(
    initialPageInfo
  );
  const [loadedUserLogsPageInfo, setLoadedUserLogsPageInfo] = useState();
  const [tableFilterValues, setTableFilterValues] = useState([]);

  // data
  const [fetchingTimeLogs, setFetchingTimeLogs] = useState(true);
  const [fetchingMoreTimeLogs, setFetchingMoreTimeLogs] = useState(false);
  const [timeLogs, setTimeLogs] = useState([]);
  const [fetchingUserStats, setFetchingUserStats] = useState(false);
  const [userStats, setUserStats] = useState({});

  // select logs
  const [selectAllMode, setSelectAllmode] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [unselectedLogs, setUnselectedLogs] = useState([]);

  const onAddNewTimeLogClick = () => {
    setAddEditTimeLogMeta({
      mode: "add",
      log: { status: isAdmin(user?.role) ? "Approved" : "Unapproved" },
    });
  };

  const onEditLogClick = (log) => {
    if (
      !isAdmin(user?.role) &&
      log?.status === "Unapproved" &&
      log?.entryOrigin === "Clocked"
    ) {
      setAddEditTimeLogMeta({
        mode: "update",
        log: { ...log, onlyComments: true },
      });
    } else {
      setAddEditTimeLogMeta({
        mode: "update",
        log,
      });
    }
  };

  const onDeleteTimeLogClick = (logToDelete) => {
    setDeleteLogMeta({ logToDelete });
  };

  const tabs = useMemo(
    () =>
      [
        {
          label: "User report",
          value: "user",
          Icon: PersonCircle,
          hide: user?.role !== ADMIN_ROLE && user?.role !== MANAGER_ROLE,
        },
        {
          label: "Job report",
          value: "job",
          Icon: Laptop,
          hide: user?.role !== ADMIN_ROLE && user?.role !== MANAGER_ROLE,
        },
        { label: "All Time Logs", value: "allTimeLogs", Icon: CalendarDate },
        {
          label: "Financial Dashboard",
          value: "financialDashboard",
          Icon: BarChart,
          hide: user?.role !== ADMIN_ROLE,
        },
      ].filter((t) => !t.hide),
    [user]
  );

  const onToggleSelectAll = (checked) => {
    if (checked && selectedLogs.length > 0) {
      setSelectedLogs([]);
      return;
    } else if (!checked && unselectedLogs.length > 0) {
      setUnselectedLogs([]);
    }
    setSelectAllmode(checked);
  };
  const onToggleSelectLog = (log) => {
    let alreadyInSelectedList = findIfObjectIsInArrayOrNot(selectedLogs, log);
    let alreadyInUnSelectedList = findIfObjectIsInArrayOrNot(
      unselectedLogs,
      log
    );
    if (selectAllMode) {
      setUnselectedLogs(
        alreadyInUnSelectedList
          ? unselectedLogs.filter((c) => c._id !== log._id)
          : [...unselectedLogs, log]
      );
    } else {
      setSelectedLogs(
        alreadyInSelectedList
          ? selectedLogs.filter((c) => c._id !== log._id)
          : [...selectedLogs, log]
      );
    }
  };

  const findIfLogIsSelected = (log) => {
    let alreadyInSelectedList = findIfObjectIsInArrayOrNot(selectedLogs, log);
    let alreadyInUnSelectedList = findIfObjectIsInArrayOrNot(
      unselectedLogs,
      log
    );
    if (selectAllMode) {
      return !alreadyInUnSelectedList;
    } else {
      return alreadyInSelectedList;
    }
  };

  const userTimeLogColumns = useMemo(
    () =>
      userTimeLogTableColumns({
        onDeleteTimeLogClick,
        onEditLogClick,
        totalCost,
        users,
        selectAllMode,
        findIfLogIsSelected,
        onToggleSelectAll,
        onToggleSelectLog,
      }),
    [
      onDeleteTimeLogClick,
      onEditLogClick,
      totalCost,
      users,
      findIfLogIsSelected,
      onToggleSelectAll,
      onToggleSelectLog,
      selectAllMode,
    ]
  );

  function handleOnDateChange(ranges) {
    setDateRange(ranges?.selection);
  }

  const fetchDashboardStats = async () => {
    setFetchingUserStats(true);

    const toTexasISO = (date) => {
      // Create a new Date object with the same time as the input
      const texasDate = new Date(date.getTime());

      // Determine if the date falls within Daylight Saving Time for Texas
      const month = date.getMonth();
      const day = date.getDate();
      const dayOfWeek = date.getDay(); // 0 for Sunday, 1 for Monday, etc.

      // Check if the date is between the second Sunday in March and the first Sunday in November
      const isDST =
        (month > 2 && month < 10) || // April to October are always in DST
        (month === 2 && day - dayOfWeek >= 7) || // Second Sunday in March or later
        (month === 10 && day - dayOfWeek < 7); // Before the first Sunday in November

      const texasOffset = isDST ? 300 : 360; // 300 mins for CDT (UTC-5), 360 mins for CST (UTC-6)

      // Calculate the offset difference and adjust the time to Texas timezone
      const offsetDifference = date.getTimezoneOffset() - texasOffset;
      texasDate.setMinutes(texasDate.getMinutes() - offsetDifference);

      return texasDate;
    };

    let reqBody = {
      filter: {
        startDateTime: dateRange?.startDate
          ? toTexasISO(dateRange.startDate)
          : null,
        endDateTime: dateRange?.endDate ? toTexasISO(dateRange.endDate) : null,
        status: includeDisabledLogs ? ["Approved", "Unapproved"] : ["Approved"],
      },
    };
    let { response, error } = await userServices.getUserStats(reqBody);

    setFetchingUserStats(false);

    if (error) {
      return toast.error(error);
    }

    const { userResult = [] } = response;
    setUserStats(response);
  };

  const fetchTimeLogs = async () => {
    if (!toLoadUserLogsPageInfo) return;

    // If an old API call is in progress, abort it
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const pageToFetch = toLoadUserLogsPageInfo?.pageNumber;
    const loadingMoreLogs = pageToFetch > 1;

    if (loadingMoreLogs) {
      setFetchingMoreTimeLogs(true);
    } else {
      setFetchingTimeLogs(true);
    }

    const controller = new AbortController();
    const { signal } = controller;

    abortControllerRef.current = controller;

    const requestBody = {
      filter: tableFilterValues,
      sort: {
        key: "startDateTime",
        order: "desc",
      },
      pageSize: DEFAULT_PAGE_SIZE,
      pageNumber: pageToFetch,
    };

    let { response, error } = await timerTrackerService.getTimeLogs(
      requestBody,
      signal
    );

    if (error === ABORT_ERROR) return;

    setFetchingTimeLogs(false);
    setFetchingMoreTimeLogs(false);

    if (error) {
      return toast.error(error);
    }

    const { pageNumber, totalPages, results = [] } = response;
    setLoadedUserLogsPageInfo({ totalPages, pageNumber });
    setTimeLogs((prevLogs) =>
      loadingMoreLogs ? [...prevLogs, ...results] : results
    );

    const newTotalCost = results.reduce((sum, log) => {
      const payment = calculatePayment(
        log?.startDateTime,
        log?.endDateTime,
        log?.hourlyWage
      );
      return sum + (payment || 0);
    }, 0);

    setTotalCost(newTotalCost);
  };
  const [confirmBatchLogUpdate, setConfirmBatchLogUpdate] = useState(null);

  const onApproveSelectedLogClick = () => {
    setConfirmBatchLogUpdate({
      showProgress: false,
    });
  };

  const batchUpdateLogs = async () => {
    let logsToUpdate = selectAllMode
      ? timeLogs
          ?.filter(({ _id }) => !unselectedLogs?.some((l) => l._id === _id))
          .map(({ _id }) => _id)
      : selectedLogs?.map(({ _id }) => _id);

    setConfirmBatchLogUpdate({ ...confirmBatchLogUpdate, showProgress: true });
    let updates = logsToUpdate.map((id) => ({
      jobId: id,
      jobUpdates: { status: "Approved" },
    }));
    let { response, error } = await timerTrackerService.batchUpdate(updates);
    if (error) {
      setConfirmBatchLogUpdate({
        ...confirmBatchLogUpdate,
        showProgress: false,
      });
      return toast.error(error);
    }
    if (response) {
      const updatedLogsMap = new Map(response.map((log) => [log._id, log]));

      const updatedLogs = timeLogs.map(
        (log) => updatedLogsMap.get(log._id) || log
      );

      setTimeLogs(updatedLogs);
      setConfirmBatchLogUpdate(null);
    }
  };

  const deleteLog = async () => {
    let { logToDelete } = deleteLogMeta;
    setDeleteLogMeta({ ...deleteLogMeta, showProgress: true });
    let { response, error } = await timerTrackerService.deleteLog(
      logToDelete?._id
    );
    if (error) {
      console.log({ error });
      setDeleteLogMeta({ ...deleteLogMeta, showProgress: false });
      return toast.error(error);
    }
    if (response) {
      let updatedLogs = timeLogs?.filter((l) => l?._id !== logToDelete?._id);
      setTimeLogs(updatedLogs);
      setDeleteLogMeta(null);
    }
  };

  const addEditLog = async (log) => {
    let { mode } = addEditTimeLogMeta;
    let addMode = mode === "add";
    setAddEditTimeLogMeta({ ...addEditTimeLogMeta, showProgress: true });
    let { response, error } = addMode
      ? await timerTrackerService.addLog(log)
      : await timerTrackerService.updateLog(log);

    if (error) {
      setAddEditTimeLogMeta({ ...deleteLogMeta, showProgress: false });
      return toast.error(error);
    }
    if (response) {
      let updatedLogs = updateItemsInArray(timeLogs, response, "_id");
      setTimeLogs(updatedLogs);
      setAddEditTimeLogMeta(null);
    }
  };

  const loadMoreData = () => {
    if (!loadedUserLogsPageInfo || fetchingMoreTimeLogs || fetchingTimeLogs)
      return;

    if (loadedUserLogsPageInfo.totalPages < loadedUserLogsPageInfo.pageNumber)
      return;

    setToLoadUserLogsPageInfo({
      ...loadedUserLogsPageInfo,
      pageNumber: loadedUserLogsPageInfo.pageNumber + 1,
    });
  };

  useEffect(() => {
    setToLoadUserLogsPageInfo({ ...initialPageInfo });
  }, [tableFilterValues]);

  useEffect(() => {
    fetchDashboardStats();
  }, [dateRange, includeDisabledLogs]);

  useEffect(() => {
    fetchTimeLogs();
  }, [toLoadUserLogsPageInfo]);

  return (
    <div className="p-3 bg-white">
      <div className="rounded border p-1">
        <TimeLogActions
          tabs={tabs}
          activeTab={activeView}
          onTabChange={setActiveView}
          dateRange={dateRange}
          onDateRangeChange={handleOnDateChange}
          includeDisabledLogs={includeDisabledLogs}
          onIncludeDisabledLogsCheckedChange={setIncludeDisabledLogs}
          onAddNewTimeLogClick={onAddNewTimeLogClick}
          onApproveSelectedLogClick={onApproveSelectedLogClick}
          selectedLogs={selectedLogs}
        />
        <hr className="my-1" />
        {activeView === "user" && (
          <ViewStatByUser
            fetchingUserStats={fetchingUserStats}
            data={userStats?.userResult}
          />
        )}{" "}
        {activeView === "job" && (
          <ViewStatByJob
            fetchingUserStats={fetchingUserStats}
            data={userStats?.jobResult}
          />
        )}
        {activeView === "allTimeLogs" && (
          <>
            <DataTable
              rowKey="_id"
              maxTableHeight={`calc(100vh - 150px)`}
              // bottomOffset={300}
              loadingFirstPageData={fetchingTimeLogs}
              data={timeLogs}
              columns={userTimeLogColumns}
              loadingMoreData={fetchingMoreTimeLogs}
              onBottomReached={loadMoreData}
              onFilterValuesChange={setTableFilterValues}
              filterValues={tableFilterValues}
              allowSort={false}
              striped
            />{" "}
            <AlertModal
              show={Boolean(deleteLogMeta)}
              onHide={() => setDeleteLogMeta(null)}
              onDismissClick={() => setDeleteLogMeta(null)}
              onContinueClick={deleteLog}
              alertText={"Are you sure you want to delete this log?"}
              showProgress={deleteLogMeta?.showProgress}
            />
            <AddEditTimeTrackerModal
              show={Boolean(addEditTimeLogMeta)}
              onHide={() => setAddEditTimeLogMeta(null)}
              values={addEditTimeLogMeta?.log}
              mode={addEditTimeLogMeta?.mode}
              showProgress={addEditTimeLogMeta?.showProgress}
              onSubmit={addEditLog}
            />
            <AlertModal
              show={Boolean(confirmBatchLogUpdate)}
              onHide={() => setConfirmBatchLogUpdate(null)}
              onDismissClick={() => setConfirmBatchLogUpdate(null)}
              onContinueClick={batchUpdateLogs}
              alertText={"Are you sure you want to approve all selected logs?"}
              showProgress={confirmBatchLogUpdate?.showProgress}
            />
          </>
        )}
        {activeView === "financialDashboard" && (
          <FinancialDashboard
            dateRange={dateRange}
            includeDisabledLogs={includeDisabledLogs}
          />
        )}
      </div>
    </div>
  );
};

export default BrowseUserTimeLogs;
